import React, { useMemo, useState, useEffect } from 'react';
import SendIcon from '@mui/icons-material/Send';
import CircularProgress from '@mui/material/CircularProgress';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

interface EmailSenderLetterPreviewProps {
  previewHtml: string;
  onClose: () => void;
  onSend: () => void;
  isSending?: boolean;
  sendError?: string | null;
  sendSuccess?: string | null;
  isStatusVisible?: boolean;
  isClosing?: boolean;
  closeDelayMs?: number;
}

export const EmailSenderLetterPreview: React.FC<EmailSenderLetterPreviewProps> = ({
  previewHtml,
  onClose,
  onSend,
  isSending = false,
  sendError,
  sendSuccess,
  isStatusVisible = false,
  isClosing = false,
  closeDelayMs = 2000
}) => {

  const [progressStarted, setProgressStarted] = useState(false);

  useEffect(() => {
    if (isClosing) {
      const timer = setTimeout(() => {
        setProgressStarted(true);
      }, 50);
      return () => clearTimeout(timer);
    } else {
      setProgressStarted(false);
    }
  }, [isClosing]);

  const safePreviewHtml = useMemo(() => {
    return previewHtml + `
      <style>
        a {
          pointer-events: none !important;
          cursor: default !important;
        }
      </style>
    `;
  }, [previewHtml]);

  const statusMessage = sendError
    ? { type: 'error' as const, text: sendError }
    : sendSuccess
      ? { type: 'success' as const, text: sendSuccess }
      : null;

  const isMessageVisible = statusMessage
    ? statusMessage.type === 'success'
      ? isStatusVisible
      : true
    : false;

  return (
    <div className="col-span-7 h-full relative bg-gray-100 pt-6 flex flex-col">

       <iframe
          title="Email Preview"
          srcDoc={safePreviewHtml}
          className="flex-1 w-full border-none block"
          style={{ backgroundColor: '#ffffff' }}
       />

       <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-20 flex flex-col items-center gap-3 w-full px-4">
           {/* Сообщение об успехе/ошибке */}
           {statusMessage && (
             <div
               className={`
                 flex flex-col items-center gap-2 px-5 py-3 rounded-xl shadow-lg border text-sm font-medium transition-all duration-300
                 ${statusMessage.type === 'error'
                   ? 'bg-red-50 border-red-200 text-red-800'
                   : 'bg-emerald-50 border-emerald-200 text-emerald-800'}
                 ${isMessageVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}
               `}
             >
               <div className="flex items-center gap-2">
                 {statusMessage.type === 'error'
                   ? <ErrorOutlineIcon fontSize="small" className="text-red-500" />
                   : <CheckCircleIcon fontSize="small" className="text-emerald-500" />
                 }
                 <span>{statusMessage.text}</span>
               </div>

               {/* Индикатор закрытия при успешной отправке */}
               {isClosing && statusMessage.type === 'success' && (
                 <div className="flex flex-col items-center gap-1.5 mt-1 w-full">
                   <span className="text-xs text-emerald-600">
                     Возврат к списку заявок...
                   </span>
                   {/* Плавный прогресс-бар */}
                   <div className="w-56 h-1.5 bg-emerald-200 rounded-full overflow-hidden">
                     <div
                       className="h-full bg-emerald-500 rounded-full"
                       style={{
                         width: progressStarted ? '100%' : '0%',
                         transition: progressStarted
                           ? `width ${closeDelayMs}ms linear`
                           : 'none'
                       }}
                     />
                   </div>
                 </div>
               )}
             </div>
           )}

           <div className="p-1.5 bg-white/90 backdrop-blur-sm border border-gray-200 rounded-xl shadow-lg flex items-center gap-2">
               <button
                 onClick={onClose}
                 disabled={isClosing}
                 className={`px-4 py-2 rounded-lg transition text-sm font-medium outline-none ${
                   isClosing
                     ? 'text-gray-400 cursor-not-allowed'
                     : 'text-gray-700 hover:bg-gray-100'
                 }`}
               >
                 {isClosing ? 'Закрытие...' : 'Отменить'}
               </button>

              <button
                onClick={onSend}
                disabled={isSending || isClosing}
                className={`px-4 py-2 rounded-lg transition text-sm font-medium flex items-center gap-2 shadow-md ml-2 outline-none ${
                  isSending || isClosing
                    ? 'bg-blue-300 text-white cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                {isSending ? (
                  <CircularProgress size={16} color="inherit" />
                ) : isClosing ? (
                  <CheckCircleIcon sx={{ fontSize: 18 }} />
                ) : (
                  <SendIcon sx={{ fontSize: 18 }} />
                )}
                {isSending ? 'Отправка...' : isClosing ? 'Отправлено!' : 'Отправить все'}
               </button>
           </div>
       </div>

    </div>
  );
};